import { z } from 'zod';
import { DISCIPLINE_OPTIONS, DisciplineType, type ContactFormData, type ContactFormErrors } from './types';

// Email regex pattern matching the auth component
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const contactFormSchema = z.object({
  discipline: z.enum(DISCIPLINE_OPTIONS as [DisciplineType, ...DisciplineType[]], {
    errorMap: () => ({ message: "Please select your discipline" })
  }),
  message: z.string()
    .min(1, "Message is required")
    .min(10, "Message must be at least 10 characters")
    .max(1000, "Message must be less than 1000 characters"),
  name: z.string()
    .min(1, "Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters")
    .regex(/^[a-zA-Z\s\-'\.]+$/, "Please enter a valid name"),
  email: z.string()
    .min(1, "Email is required")
    .regex(EMAIL_REGEX, "Please enter a valid email address")
});

export type ContactFormSchema = z.infer<typeof contactFormSchema>;

export const getFieldErrors = (field: keyof ContactFormData, value: string): string | null => {
  try {
    const fieldSchema = contactFormSchema.shape[field];
    fieldSchema.parse(value);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0]?.message || "Invalid input";
    }
    return "Invalid input";
  }
};

export const validateFullForm = (data: ContactFormData): ContactFormErrors => {
  try {
    contactFormSchema.parse(data);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: ContactFormErrors = {};
      error.errors.forEach((err) => {
        const path = err.path[0] as keyof ContactFormData;
        if (path && !errors[path]) {
          errors[path] = err.message;
        }
      });
      return errors;
    }
    return { general: "Validation failed" };
  }
};

export const updateFieldValidations = (field: keyof ContactFormData, target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): boolean => {
  let { value } = target;

  // Clean name field similar to auth component
  if (field === "name") {
    value = value.replace(/[\p{Emoji}]/gu, "").trim();
  }

  const error = getFieldErrors(field, value);
  const parentGroup = target.closest(".form-group");
  const errorElement = parentGroup?.querySelector(".error-message");

  if (!parentGroup || !errorElement) {
    return !error;
  }

  parentGroup.classList.toggle("has-error", !!error);
  errorElement.textContent = error ?? "";

  return !error;
};